import { useState, useEffect } from "react";
import { Shield, Mail, Smartphone, Check, X, ChevronRight, ArrowLeft } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useAPI } from "../Context/MyContext";


const TwoFactorAuth = () => {
  const { enableTwoFactor, verifyTwoFactor, disableTwoFactor, getAllowedUserFeatures } = useAPI();
  const [step, setStep] = useState("idle");
  const [method, setMethod] = useState(null);
  const [isEnabled, setIsEnabled] = useState(false);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [otpError, setOtpError] = useState("");
  const [phoneData, setPhoneData] = useState({ countryCode: "+92", number: "" });
  const [loading, setLoading] = useState(false);

  
  useEffect(() => {
    const fetch2FAStatus = async () => {
      try {
        const user = await getAllowedUserFeatures();
  
        if (user?.isTwoFactor) {
          setIsEnabled(true);
          setStep("idle");
          setMethod(user?.twoFactorMethod || "email");
        }
      } catch (err) {
        console.error("Failed to load 2FA status", err);
      }
    };
  
    fetch2FAStatus();
  }, []);

  const handleChooseMethod = async (m) => {
    try {
      setLoading(true);

      await enableTwoFactor(m);

    

      setMethod(m);
      setStep("verify");
      setOtp(["", "", "", "", "", ""]);
      setOtpError("");

    } catch (err) {
      console.error("Enable 2FA failed:", err);
      setOtpError(err.message || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (val, idx) => {
    const newOtp = [...otp];
    newOtp[idx] = val.slice(-1);
    setOtp(newOtp);
    if (val && idx < 5) {
      document.getElementById(`otp-${idx + 1}`)?.focus();
    }
  };

  const handleOtpKeyDown = (e, idx) => {
    if (e.key === "Backspace" && !otp[idx] && idx > 0) {
      document.getElementById(`otp-${idx - 1}`)?.focus();
    }
  };

  const handleVerify = async () => {
    const code = otp.join("");

    if (code.length < 6) {
      setOtpError("Please enter the full 6-digit code.");
      return;
    }

    try {
      setLoading(true);
      setOtpError("");

      const res = await verifyTwoFactor(code);

      if (res.success) {
        setIsEnabled(true);
        setStep("done");
      } else {
        setOtpError(res.message || "Invalid OTP");
      }

    } catch (err) {
      setOtpError(err.message || "Verification failed");
    } finally {
      setLoading(false);
    }
  };


  const handleDisable = async () => {
    try {
      setLoading(true);

      await disableTwoFactor();

      setIsEnabled(false);
      setStep("idle");
      setMethod(null);
      setOtp(["", "", "", "", "", ""]);

    } catch (err) {
      console.error("Disable failed:", err);
    } finally {
      setLoading(false);
    }
  };


  const handleBack = () => {
    setStep("choose");
    setOtp(["", "", "", "", "", ""]);
    setOtpError("");
  };

  return (
    <div className="max-w-4xl mx-auto">
      <h1 className="text-2xl font-semibold mb-8 text-gray-800">Two Factor Authentication</h1>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">

        {/* Header */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-8 py-6 border-b border-gray-100">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <Shield className="text-blue-600" size={22} />
            </div>
            <div className="flex-1">
              <h2 className="text-base font-semibold text-gray-900">Secure Your Account</h2>
              <p className="text-sm text-gray-500 mt-0.5">Two-factor authentication adds an extra layer of protection.</p>
            </div>
            {/* Status badge */}
            <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${isEnabled ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-500"}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${isEnabled ? "bg-green-500 animate-pulse" : "bg-gray-400"}`} />
              {isEnabled ? "Enabled" : "Disabled"}
            </div>
          </div>
        </div>

        <div className="px-8 py-8">
          <AnimatePresence mode="wait">

            {/* ── IDLE: not yet enabled ── */}
            {step === "idle" && !isEnabled && (
              <motion.div key="idle"
                initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.2 }}
              >
                <p className="text-sm text-gray-500 mb-6">
                  2FA is currently <span className="font-semibold text-gray-700">not active</span> on your account. Enable it to protect your account with a verification code every time you sign in.
                </p>
                <button
                  onClick={() => setStep("choose")}
                  className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-xl transition shadow-sm"
                >
                  Enable 2FA <ChevronRight size={15} />
                </button>
              </motion.div>
            )}

            {/* ── CHOOSE method ── */}
            {step === "choose" && (
              <motion.div key="choose"
                initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.2 }}
              >
                {/* <p className="text-sm text-gray-500 mb-6">Choose how you'd like to receive your verification code.</p> */}
                {/* <p className="text-sm text-gray-500 mb-6"></p> */}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Email option */}
                  <button
                    onClick={() => handleChooseMethod("email")}
                    className="group flex items-center gap-4 p-5 rounded-xl border-2 border-gray-100 hover:border-blue-400 hover:bg-blue-50/40 transition-all text-left"
                  >
                    <div className="w-10 h-10 rounded-lg bg-blue-100 group-hover:bg-blue-200 flex items-center justify-center flex-shrink-0 transition">
                      <Mail size={18} className="text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-800">Via Email</p>
                      <p className="text-xs text-gray-400 mt-0.5">Code sent to your registered email</p>
                    </div>
                    <ChevronRight size={15} className="ml-auto text-gray-300 group-hover:text-blue-500 transition" />
                  </button>

                  {/* Phone option */}
                  {/* <button
                    onClick={() => handleChooseMethod("phone")}
                    className="group flex items-center gap-4 p-5 rounded-xl border-2 border-gray-100 hover:border-blue-400 hover:bg-blue-50/40 transition-all text-left"
                  >
                    <div className="w-10 h-10 rounded-lg bg-indigo-100 group-hover:bg-indigo-200 flex items-center justify-center flex-shrink-0 transition">
                      <Smartphone size={18} className="text-indigo-600" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-800">Via WhatsApp</p>
                      <p className="text-xs text-gray-400 mt-0.5">Code sent to your phone number</p>
                    </div>
                    <ChevronRight size={15} className="ml-auto text-gray-300 group-hover:text-blue-500 transition" />
                  </button> */}
                </div>

                <button onClick={() => setStep("idle")} className="mt-5 flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-600 transition">
                  <ArrowLeft size={13} /> Cancel
                </button>
              </motion.div>
            )}

            {/* ── VERIFY: OTP input ── */}
            {step === "verify" && (
              <motion.div key="verify"
                initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.2 }}
              >
                {/* Phone number field (only if phone method) */}
                {method === "phone" && (
                  <div className="mb-6">
                    <p className="text-[11px] font-bold tracking-widest text-gray-400 uppercase mb-2">Phone Number</p>
                    <div className="flex gap-2">
                      <select
                        value={phoneData.countryCode}
                        onChange={(e) => setPhoneData({ ...phoneData, countryCode: e.target.value })}
                        className="px-3 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-700 bg-white outline-none focus:ring-2 focus:ring-blue-400"
                      >
                        <option value="+92">🇵🇰 +92</option>
                        <option value="+1">🇺🇸 +1</option>
                        <option value="+44">🇬🇧 +44</option>
                        <option value="+91">🇮🇳 +91</option>
                      </select>
                      <input
                        type="text"
                        placeholder="Enter phone number"
                        value={phoneData.number}
                        onChange={(e) => setPhoneData({ ...phoneData, number: e.target.value })}
                        className="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-800 outline-none focus:ring-2 focus:ring-blue-400"
                      />
                    </div>
                  </div>
                )}

                <div className="bg-blue-50 border border-blue-100 rounded-xl px-5 py-4 mb-6">
                  <p className="text-sm text-blue-700 font-medium">
                    A 6-digit verification code has been sent to your <span className="font-bold">{method === "email" ? "email address" : "phone number"}</span>. Enter it below to activate 2FA.
                  </p>
                </div>

                {/* OTP boxes */}
                <div>
                  <p className="text-[11px] font-bold tracking-widest text-gray-400 uppercase mb-3">Verification Code</p>
                  <div className="flex gap-2 mb-2">
                    {otp.map((digit, i) => (
                      <input
                        key={i}
                        id={`otp-${i}`}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handleOtpChange(e.target.value, i)}
                        onKeyDown={(e) => handleOtpKeyDown(e, i)}
                        className="w-11 h-12 text-center text-lg font-bold border-2 border-gray-200 rounded-xl outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition text-gray-800"
                      />
                    ))}
                  </div>
                  {otpError && <p className="text-xs text-red-500 mt-1">{otpError}</p>}
                </div>

                <div className="flex items-center gap-3 mt-6">
                  <button
                    onClick={handleVerify}
                    disabled={loading}
                    className={`flex items-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-xl text-white transition shadow-sm ${loading ? "bg-blue-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"}`}
                  >
                    {loading ? "Verifying..." : <><Check size={15} /> Verify & Activate</>}
                  </button>
                  <button onClick={handleBack} className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-600 transition">
                    <ArrowLeft size={13} /> Back
                  </button>
                </div>
              </motion.div>
            )}

            {/* ── DONE: 2FA active ── */}
            {(step === "done" || (step === "idle" && isEnabled)) && (
              <motion.div key="done"
                initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.2 }}
              >
                <div className="flex items-center gap-3 mb-6 p-4 bg-green-50 border border-green-100 rounded-xl">
                  <div className="w-9 h-9 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
                    <Check size={16} className="text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-green-800">2FA is Active</p>
                    <p className="text-xs text-green-600 mt-0.5">
                      Your account is protected via <span className="font-semibold">{method === "email" ? "Email" : "Phone / WhatsApp"}</span>
                    </p>
                  </div>
                </div>

                {/* Active method display */}
                <div className="mb-6">
                  <p className="text-[11px] font-bold tracking-widest text-gray-400 uppercase mb-1">Active Method</p>
                  <p className="text-sm font-semibold text-gray-800">{method === "email" ? "Email Verification" : `Phone / WhatsApp (${phoneData.countryCode} ${phoneData.number})`}</p>
                </div>

                <button
                  onClick={handleDisable}
                  className="flex items-center gap-2 px-5 py-2.5 border border-red-200 text-red-500 hover:bg-red-50 text-sm font-semibold rounded-xl transition"
                >
                  <X size={14} /> Disable 2FA
                </button>
              </motion.div>
            )}

          </AnimatePresence>

          {/* Security tips — always visible */}
          <div className="mt-10 pt-6 border-t border-gray-100">
            <p className="text-[11px] font-bold tracking-widest text-gray-400 uppercase mb-4">Security Tips</p>
            <div className="space-y-2.5">
              {[
                "Keep your recovery method up to date at all times.",
                "Use a phone number or email you have constant access to.",
                "Never share your verification codes with anyone.",
              ].map((tip) => (
                <div key={tip} className="flex items-start gap-3">
                  <div className="w-4 h-4 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check size={10} className="text-green-600" />
                  </div>
                  <p className="text-sm text-gray-500">{tip}</p>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default TwoFactorAuth;